import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { ControlDate } from '../types/controle-datas';
import { PHASE_IDS } from '../utils/constants';

/**
 * Hook para buscar uma data de controle específica por _id
 *
 * @param id - O _id da data de controle a ser buscada
 * @returns A data de controle encontrada ou undefined
 *
 * @example
 * const etapaPreElbb = useControlDate('etapa_pre_elbb');
 * if (etapaPreElbb) {
 *   console.log(etapaPreElbb.titulo, etapaPreElbb.dataInicio);
 * }
 */
export function useControlDate(id: string): ControlDate | undefined {
  const { controlDates } = useAuth();

  const controlDate = useMemo(() => {
    if (!controlDates || controlDates.length === 0) {
      return undefined;
    }

    return controlDates.find((date) => date._id === id);
  }, [controlDates, id]);

  return controlDate;
}

/**
 * Hook para buscar múltiplas datas de controle por _ids
 *
 * @param ids - Array de _ids das datas de controle
 * @returns Array de datas de controle encontradas
 *
 * @example
 * const [etapaPreElbb, etapaElbb] = useControlDates(['etapa_pre_elbb', 'etapa_elbb']);
 */
export function useControlDates(ids: string[]): (ControlDate | undefined)[] {
  const { controlDates } = useAuth();

  const dates = useMemo(() => {
    if (!controlDates || controlDates.length === 0) {
      return ids.map(() => undefined);
    }

    return ids.map((id) => controlDates.find((date) => date._id === id));
  }, [controlDates, ids]);

  return dates;
}

/**
 * Hook para verificar se uma data de controle está ativa
 *
 * @param id - O _id da data de controle
 * @returns true se a data está ativa, false caso contrário
 *
 * @example
 * const inscricoesAbertas = useIsDateActive('etapa_pre_elbb');
 */
export function useIsDateActive(id: string): boolean {
  const controlDate = useControlDate(id);

  return useMemo(() => {
    if (!controlDate) return false;

    const now = new Date();
    const inicio = new Date(controlDate.dataInicio);
    const fim = controlDate.dataFim ? new Date(controlDate.dataFim) : null;

    if (fim) {
      return now >= inicio && now <= fim;
    }

    return now >= inicio;
  }, [controlDate]);
}

/**
 * Structured result of all phase states.
 * Each boolean tells you if that specific phase is currently active.
 */
export interface ActivePhases {
  // ELBB
  elbbPreRegistration: boolean;
  elbbRegistrationPeriod: boolean;
  elbbConfirmation: boolean;
  elbbInscriptionPeriod: boolean;
  elbbVotingStage1: boolean;
  elbbVotingStage2: boolean;
  // Conexao
  conexaoPreRegistration: boolean;
  conexaoRegistrationPeriod: boolean;
  conexaoManagerApproval: boolean;
  conexaoConfirmation: boolean;
  // Etapa 3
  etapa3premio: boolean;
  // Derived helpers
  isAnyElbbActive: boolean;
  canAccessCadastroELBB: boolean;
  isAnyConexaoActive: boolean;
  canAccessCadastroConexao: boolean;
}

/**
 * Hook that returns all phase states at once.
 * Call once at the routing/page level, pass down or use directly.
 *
 * @example
 * const phases = useActivePhases();
 * if (phases.elbbRegistrationPeriod) { ... }
 * if (phases.conexaoPreRegistration) { ... }
 */
export function useActivePhases(): ActivePhases {
  // ELBB
  const elbbPreRegistration = useIsDateActive(PHASE_IDS.ELBB_PRE_REGISTRATION);
  const elbbRegistrationPeriod = useIsDateActive(PHASE_IDS.ELBB_REGISTRATION_PERIOD);
  const elbbConfirmation = useIsDateActive(PHASE_IDS.ELBB_CONFIRMATION);
  const elbbInscriptionPeriod = useIsDateActive(PHASE_IDS.ELBB_INSCRIPTION);
  const elbbVotingStage1 = useIsDateActive(PHASE_IDS.ELBB_VOTING_STAGE1);
  const elbbVotingStage2 = useIsDateActive(PHASE_IDS.ETAPA_2_PREMIO);
  // Conexao
  const conexaoPreRegistration = useIsDateActive(PHASE_IDS.CONEXAO_PRE_REGISTRATION);
  const conexaoRegistrationPeriod = useIsDateActive(PHASE_IDS.CONEXAO_REGISTRATION_PERIOD);
  const conexaoManagerApproval = useIsDateActive(PHASE_IDS.CONEXAO_MANAGER_APPROVAL);
  const conexaoConfirmation = useIsDateActive(PHASE_IDS.CONEXAO_CONFIRMATION);
  // Etapa 3
  const etapa3premio = useIsDateActive(PHASE_IDS.ETAPA_3_PREMIO);

  return useMemo(() => ({
    elbbPreRegistration,
    elbbRegistrationPeriod,
    elbbConfirmation,
    elbbInscriptionPeriod,
    elbbVotingStage1,
    elbbVotingStage2,
    conexaoPreRegistration,
    conexaoRegistrationPeriod,
    conexaoManagerApproval,
    conexaoConfirmation,
    etapa3premio,
    isAnyElbbActive: elbbPreRegistration || elbbRegistrationPeriod || elbbConfirmation,
    canAccessCadastroELBB: elbbRegistrationPeriod || elbbPreRegistration || elbbConfirmation,
    isAnyConexaoActive: conexaoPreRegistration || conexaoRegistrationPeriod || conexaoManagerApproval || conexaoConfirmation,
    canAccessCadastroConexao: conexaoPreRegistration, // Mudado para etapa_pre_conexao
  }), [
    elbbPreRegistration, elbbRegistrationPeriod, elbbConfirmation, elbbInscriptionPeriod, elbbVotingStage1, elbbVotingStage2,
    conexaoPreRegistration, conexaoRegistrationPeriod, conexaoManagerApproval, conexaoConfirmation,
    etapa3premio,
  ]);
}
